import { api } from '@/config/api';

export type RetainerBillingCycle = 'monthly' | 'quarterly' | 'yearly';
export type RetainerStatus = 'active' | 'paused' | 'cancelled';

export interface RetainerInvoiceRecord {
  documentUid?: string;
  periodStart: string;
  periodEnd: string;
  amount: number;
  createdAt: string;
}

export interface Retainer {
  _id: string;
  retainerUid: string;
  companyUid: string;
  name: string;
  clientContactUid: string;
  projectUid?: string;
  amount: number;
  currency: string;
  billingCycle: RetainerBillingCycle;
  startDate: string;
  nextBillingDate: string;
  includedHours?: number;
  status: RetainerStatus;
  notes?: string;
  lastInvoicedAt?: string;
  invoices: RetainerInvoiceRecord[];
  createdAt: string;
  updatedAt: string;
  hoursUsed: number;
  period: { start: string; end: string };
}

export interface ListResponse<T> { items: T[]; total: number; page: number; limit: number; }

export interface RetainersSummary {
  currency: string;
  mrr: number;
  otherCurrencies: Record<string, number>;
  activeCount: number;
  dueThisWeek: number;
  includedHours: number;
  hoursUsed: number;
}

export interface CreateRetainerPayload {
  name: string;
  clientContactUid: string;
  projectUid?: string;
  amount: number;
  currency?: string;
  billingCycle?: RetainerBillingCycle;
  startDate: string;
  nextBillingDate?: string;
  includedHours?: number;
  notes?: string;
}

export const retainersService = {
  list: async (q: { status?: string; client?: string; q?: string; page?: number; limit?: number } = {}): Promise<ListResponse<Retainer>> =>
    (await api.get('/slighter_crm/retainers', { params: q })).data,
  summary: async (): Promise<RetainersSummary> => (await api.get('/slighter_crm/retainers/summary')).data,
  get: async (retainerUid: string): Promise<Retainer> => (await api.get(`/slighter_crm/retainers/${retainerUid}`)).data,
  create: async (p: CreateRetainerPayload): Promise<Retainer> => (await api.post('/slighter_crm/retainers', p)).data,
  update: async (retainerUid: string, p: Partial<CreateRetainerPayload>): Promise<Retainer> =>
    (await api.patch(`/slighter_crm/retainers/${retainerUid}`, p)).data,
  remove: async (retainerUid: string): Promise<void> => { await api.delete(`/slighter_crm/retainers/${retainerUid}`); },
  invoicePeriod: async (retainerUid: string): Promise<Retainer> => (await api.post(`/slighter_crm/retainers/${retainerUid}/invoice-period`)).data,
  pause: async (retainerUid: string): Promise<Retainer> => (await api.post(`/slighter_crm/retainers/${retainerUid}/pause`)).data,
  resume: async (retainerUid: string): Promise<Retainer> => (await api.post(`/slighter_crm/retainers/${retainerUid}/resume`)).data,
  cancel: async (retainerUid: string): Promise<Retainer> => (await api.post(`/slighter_crm/retainers/${retainerUid}/cancel`)).data,
};
